import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { ArrowNext } from "@/assets/icons";

const defaultClasses =
  "bg-primary text-white shadow hover:bg-primary-hover disabled:bg-grey-light disabled:text-primary-70 disabled:opacity-100";
const linkClasses =
  "text-primary inline-flex hover:text-primary-hover justify-start px-0 text-xs capitalize w-fit gap-1.5 uppercase tracking-widest hover:fill-primary-hover";

const buttonVariants = cva(
  "w-full px-[0.719rem] max-[350px]:text-base flex flex-row items-center justify-center gap-2 whitespace-wrap xs:whitespace-nowrap rounded text-lg max-[250px]:text-sm transition-colors font-medium focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 overflow-auto uppercase min-h-[3.125rem] overflow-x-clip drop-shadow-[0_4px_6px_hsl(var(--shadow-light))]",
  {
    variants: {
      variant: {
        default: defaultClasses,
        secondary:
          "text-primary bg-white border-2 border-primary foreground shadow-sm hover:text-primary-foreground hover:bg-primary active:text-primary-foreground active:bg-primary disabled:border-disabled-border disabled:text-primary-70 disabled:opacity-100",
        tertiary:
          "text-primary bg-white border-2 border-primary foreground shadow-sm hover:text-primary-foreground hover:bg-primary active:text-primary-foreground active:bg-primary disabled:border-disabled-border disabled:text-primary-70 disabled:opacity-100",
        ghost: "hover:bg-grey-light hover:text-accent-foreground",
        destructive: "hover:bg-destructive-foreground text-destructive",
        link: linkClasses,
        inverse:
          "text-primary bg-primary-foreground hover:bg-primary-hover hover:text-primary-foreground border-invisible disabled:text-primary-70 disabled:opacity-100 disabled:bg-grey-light",
        none: "",
      },
      size: {
        default: "py-2",
        desktop: "md:w-[26.25rem] w-full mx-auto py-2",
        sm: "h-8 rounded px-3 text-xs",
        lg: "h-10 rounded px-8",
        icon: "h-9 w-9",
        link: "min-h-6",
      },
      sticky: {
        false: null,
        true: "sticky bottom-3 md:bottom-0 md:static",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export type Variants = VariantProps<typeof buttonVariants>["variant"];

const variantIcons = ({ variant }: { variant: Variants }) => {
  switch (variant) {
    case "secondary":
    case "ghost":
    case "destructive":
    case "link":
      return;
    case "tertiary":
    case "inverse":
    default:
      return <ArrowNext className="ml-1 fill-current" />;
  }
};

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  icon?: React.ReactNode;
  processing?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      icon,
      children,
      asChild = false,
      type = "button",
      sticky,
      processing = false,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Component = asChild ? Slot : "button";
    return (
      <Component
        className={cn(buttonVariants({ variant, size, className, sticky }))}
        ref={ref}
        type={type}
        disabled={disabled || processing}
        {...props}
      >
        {children}
        {icon ??
          (processing ? (
            <div className="my-auto h-[20px] w-[20px] animate-[spin_2s_linear_infinite] rounded-full border-[3px] border-t-primary-70" />
          ) : (
            variantIcons({ variant })
          ))}
      </Component>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
